import { getQueryKey } from "@trpc/react-query";
import type { DataQueryUtils } from "../core/session";
import { cachedAlbums } from "@sqlite/schema";
import {
  albumSchema,
  playlistWithSongsSchema,
  type Album,
  type PlaylistWithSongs,
} from "@libs/wire";
import { trpc } from "../core/trpc";
import type { AuthSession } from "../../runtime/authSession";
import type { createDurableCache } from "@utils/durableCache/createDurableCache";
import { isNetworkClass } from "@utils/durableCache/createDurableCache";
import { APP_ONLY_PLAYLIST_IDS } from "@utils/collections/playlists";
import { InvalidOptionsError } from "@utils/InvalidOptionsError";
import { waitForAuthReady } from "@utils/auth/waitForAuthReady";

type CollectionData = {
  cache: ReturnType<typeof createDurableCache>;
  utils: DataQueryUtils;
  auth: AuthSession;
};

export const albumsQueryKey = (albumIds?: string[]) =>
  getQueryKey(trpc.catalog.albums.getById, albumIds);

export const albumsQuery = (data: CollectionData, albumIds?: string[]) => {
  const ids = albumIds ?? [];
  return data.cache.durableEntityQueryOptions<Album>({
    queryKey: albumsQueryKey(albumIds),
    ids,
    entityTable: cachedAlbums,
    idColumn: cachedAlbums.albumId,
    dataColumn: cachedAlbums.data,
    fetch: async (fetchIds) =>
      data.utils.catalog.albums.getById.fetch(fetchIds, {
        networkMode: "always",
      }),
    schema: albumSchema,
    getId: (a) => a.id,
    label: "useAlbums",
  });
};

export const playlistQueryKey = (id: string) =>
  getQueryKey(trpc.catalog.playlists.getById, [id]);

export const playlistQuery = (data: CollectionData, id: string) =>
  data.cache.durableQueryOptions({
    fallbackOnError: isNetworkClass,
    queryKey: playlistQueryKey(id),
    queryFn: async (): Promise<PlaylistWithSongs> => {
      if (!id) {
        throw new InvalidOptionsError("playlists.getById");
      }

      if (APP_ONLY_PLAYLIST_IDS.includes(id)) {
        // App-only playlists (continuous_listening, explore_feed) live entirely
        // client-side. The hook disables the query for these ids — if queryFn
        // is invoked anyway (suspense/prefetch path), throw so the caller
        // sees a clear error rather than an "undefined data" warning.
        throw new InvalidOptionsError(
          `playlists.getById: ${id} is app-only, no server data`,
        );
      }

      // Wait for auth to initialize so an existing session (if any) is
      // attached to the request, then fetch regardless of login state.
      // `catalog.playlists.getById` is a public procedure — anonymous
      // callers are supported (e.g. opening a shared playlist link while
      // logged out) and the server applies visibility rules per-caller.
      await waitForAuthReady(data.auth);
      const result = await data.utils.catalog.playlists.getById.fetch([id], {
        staleTime: 0,
        networkMode: "always",
      });
      const playlist = result?.[0];
      if (!playlist) {
        // A missing playlist must not overwrite a previous durable value.
        throw new Error(`playlists.getById returned no playlist for ${id}`);
      }
      return playlist;
    },
    schema: playlistWithSongsSchema,
  });
